import React, { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Label,
  Dropdown,
  Option,
  Checkbox,
  Field,
  Spinner,
  Tooltip,
} from '@fluentui/react-components';
import {
  DismissRegular,
} from '@fluentui/react-icons';
import { NewsCreateDTO, NewsListItem, NewsTypes, NewsUpdateDTO } from '../../models/news';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useAppContext } from '../../AppContext';
import { Module } from '../../models/module';
import { Department, Organization } from '../../models/organizations';
import { updateDocument } from '../../services/documentService';
import { createNews, updateNews } from '../../services/newsService';
import { create } from 'domain';
import { on } from 'events';

interface NewsFormData {
  newsType: number;
  header: string;
  subject: string;
  body: string;
  highlighted: boolean;
  publishDate: Date;
  unpublishDate: string;
  departmentID?: number;
  moduleID?: number;
}

interface NewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  news?: NewsListItem;
  onSuccess: () => void;
}

export const CreateEditNews: FC<NewsModalProps> = ({
  isOpen,
  onClose,
  news,
  onSuccess,
}) => {
  const categories = NewsTypes;
  const {user} = useAppContext();

  const [tenderingModules, setTenderingModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  
  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm<NewsFormData>({
    defaultValues: {
      newsType: 1,
      header: '',
      subject: '',
      body: '',
      highlighted: false,
      publishDate: new Date(),
      unpublishDate: '',
      moduleID: undefined,
    }
  });

  useEffect(() => {
    if(user && isOpen){
      setTenderingModules(user.modules.filter((m: Module) => m.moduleType == 'Tendering'));
    }
  }, [user, isOpen])

  const selectedNewsType = watch('newsType');
  const departmentID = watch('departmentID');

  // Reset departmentID and moduleID when category changes
  useEffect(() => {
    if (selectedNewsType !== 3) {
      setValue('moduleID', undefined);
    }
  }, [selectedNewsType, setValue]);

  useEffect(() => {
    if (news) {
      reset({
        ...news,
        newsType: news.newsTypeID,
        publishDate: news.publishDate ? new Date(news.publishDate) : new Date(),
      });
    } else {
      // Reset to default values when creating new
      reset({
        newsType: 1,
        header: '',
        subject: '',
        body: '',
        highlighted: false,
        publishDate: new Date(),
        unpublishDate: '',
        moduleID: undefined
      });
    }
  }, [news, reset, isOpen]);

  const onSubmit = async (data: NewsFormData) => {

    setLoading(true);
    const newsData: NewsCreateDTO = {
      NewsTypeID: data.newsType,
      Header: data.header,
      Subject: data.subject,
      Body: data.body,
      Highlighted: data.highlighted,
      PublishDate: data.publishDate ? data.publishDate.toISOString() : undefined,
      UnpublishDate: data.unpublishDate ? new Date(data.unpublishDate).toISOString() : undefined,
      ModuleID: data.moduleID,
    }
    try{
      if(news?.newsID){
        const updateData: NewsUpdateDTO = {
          NewsID: news.newsID,
          ...newsData,
        }
        await updateNews(updateData);
      }
      else{
        await createNews(newsData);
      }
    }
    catch(err){
      console.log(err);
    }
    finally{
      setLoading(false);
      onSuccess();
      onClose();
    }

  };


  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(event, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Tooltip content="Stäng" relationship="label">
                <Button
                  appearance="subtle"
                  shape="circular"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={onClose}
                />
              </Tooltip>
            }
          >
            {news ? 'Redigera nyhet' : 'Skapa nyhet'}
          </DialogTitle>
          <DialogContent className='overflow-auto max-h-[80vh]'>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <Field
                label="Kategori"
                required
                validationMessage={errors.newsType?.message}
              >
                <Controller
                  name="newsType"
                  control={control}
                  rules={{ required: 'Kategori är obligatoriskt' }}
                  render={({ field }) => (
                    <Dropdown
                      placeholder="Välj kategori"
                      value={categories.find(c => c.id === field.value)?.name || ''}
                      selectedOptions={[field.value.toString()]}
                      onOptionSelect={(e, data) => field.onChange(Number(data.optionValue))}
                    >
                      {categories.map(category => (
                        <Option key={category.id} value={category.id.toString()}>
                          {category.name}
                        </Option>
                      ))}
                    </Dropdown>
                  )}
                />
              </Field>

              {/* {selectedNewsType === 2 && (
                <Field
                  label="Avdelning"
                  required
                  validationMessage={errors.departmentID?.message}
                >
                  <Controller
                    name="departmentID"
                    control={control}
                    rules={{ required: 'Avdelning är obligatoriskt' }}
                    render={({ field }) => (
                      <Dropdown
                        placeholder="Välj avdelning"
                        value={departments.find(d => d.departmentID === field.value)?.departmentName || ''}
                        selectedOptions={field.value ? [field.value.toString()] : []}
                        onOptionSelect={(e, data) => field.onChange(Number(data.optionValue))}
                        positioning={{autoSize: false}}
                        listbox={{
                          style: {
                            maxHeight: '300px',
                            overflowY: 'auto'
                          },
                        }}
                      >
                        {departments.map(department => (
                          <Option key={department.departmentID} value={department.departmentID.toString()}>
                            {department.departmentName}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  />
                </Field>
              )} */}

              {selectedNewsType === 3 && (
                <Field
                  label="Avtal"
                  required
                  validationMessage={errors.moduleID?.message}
                >
                  <Controller
                    name="moduleID"
                    control={control}
                    rules={{ required: 'Avtal är obligatoriskt' }}
                    render={({ field }) => (
                      <Dropdown
                        placeholder="Välj avtal"
                        value={tenderingModules.find(m => m.moduleID === field.value)?.moduleName || ''}
                        selectedOptions={field.value ? [field.value.toString()] : []}
                        onOptionSelect={(e, data) => field.onChange(Number(data.optionValue))}
                        positioning={{autoSize: false}}
                        listbox={{
                          style: {
                            maxHeight: '300px',
                            overflowY: 'auto'
                          },
                        }}
                      >
                        {tenderingModules.map(module => (
                          <Option key={module.moduleID} value={module.moduleID.toString()}>
                            {module.moduleName}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  />
                </Field>
              )}

              <Field
                label="Rubrik"
                required
                validationMessage={errors.header?.message}
              >
                <Controller
                  name="header"
                  control={control}
                  rules={{ required: 'Rubrik är obligatoriskt' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Ange rubrik"
                    />
                  )}
                />
              </Field>

              <Field
                label="Ämne"
                required
                validationMessage={errors.subject?.message}
              >
                <Controller
                  name="subject"
                  control={control}
                  rules={{ required: 'Ämne är obligatoriskt' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Ange ämne"
                    />
                  )}
                />
              </Field>

              <Field
                label="Brödtext"
                required
                validationMessage={errors.body?.message}
              >
                <Controller
                  name="body"
                  control={control}
                  rules={{ required: 'Brödtext är obligatoriskt' }}
                  render={({ field }) => (
                    <ReactQuill
                      theme="snow"
                      className='rounded-quill-editor mb-10'
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Skriv brödtext här..."
                    />
                  )}
                />
              </Field>

              <Field>
                <Controller
                  name="highlighted"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={(_, data) => field.onChange(data.checked)}
                      label="Markerad"
                    />
                  )}
                />
              </Field>

              <Field label="Publiceringsdatum">
                <Controller
                  name="publishDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ? new Date(field.value) : undefined}
                      formatDate={(date?: Date) => { if(!date) return ''; return date.toLocaleDateString('sv-SE') }}
                      showWeekNumbers={true}
                      firstWeekOfYear={1}
                      firstDayOfWeek={1}
                      onSelectDate={field.onChange}
                      showMonthPickerAsOverlay={true} />
                        )}
                    />
              </Field>

              <Field label="Avpubliceringsdatum">
                <Controller
                  name="unpublishDate"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <DatePicker
                        style={{ width: '100%' }}
                        value={field.value ? new Date(field.value) : undefined}
                        formatDate={(date?: Date) => { if(!date) return ''; return date.toLocaleDateString('sv-SE') }}
                        showWeekNumbers={true}
                        firstWeekOfYear={1}
                        firstDayOfWeek={1}
                        allowTextInput={true}
                        onSelectDate={field.onChange}
                        showMonthPickerAsOverlay={true} />
                      {field.value && (
                        <Tooltip content="Ta bort avpubliceringsdatum" relationship="label">
                          <Button
                            appearance="subtle"
                            shape="circular"
                            size="small"
                            icon={<DismissRegular />}
                            onClick={(e) => { e.stopPropagation(); field.onChange(null); }}
                            style={{ position: 'absolute', right: '32px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}
                />
              </Field>
            </form>
          </DialogContent>
          <DialogActions className='mt-4'>
            <Tooltip content="Avbryt utan att spara" relationship="label">
              <Button disabled={loading} appearance="secondary" onClick={onClose}>
                Avbryt
              </Button>
            </Tooltip>
            <Tooltip content={news ? 'Spara ändringar' : 'Skapa nyheten'} relationship="label">
              <Button disabled={loading} appearance="primary" onClick={handleSubmit(onSubmit)}>
                {news ? 'Uppdatera' : 'Skapa'}
              </Button>
            </Tooltip>
            {loading && <Spinner />}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateEditNews;
